import { motion } from "framer-motion";
import { XCircle } from "lucide-react";

const pretty = (s) => {
  if (!s) return "Update";
  if (/^[A-Z0-9_]+$/.test(s)) {
    return s.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return s;
};

// Dynamic shipping flow: render exactly the recorded updates, newest first.
export default function Timeline({ timeline = [], status, publicView = false }) {
  const items = (timeline || [])
    .filter((t) => t.status !== "EVENT")
    .filter((t) => !publicView || !/^(courier tracking:|domestic courier:)/i.test(t.note || ""))
    .map((t, i) => ({ ...t, _i: i }))
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));

  if (status === "CANCELLED") {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-600" data-testid="timeline-cancelled">
        <XCircle className="h-5 w-5" /> <span className="font-semibold">Shipment Cancelled</span>
      </div>
    );
  }

  return (
    <div data-testid="tracking-timeline-wrap">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-bold tracking-tight">Shipping history</h3>
        <span className="text-xs text-muted-foreground font-mono-label" data-testid="timeline-count">{items.length} update{items.length === 1 ? "" : "s"}</span>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground" data-testid="timeline-empty">
          No updates yet.
        </div>
      ) : (
        <ol className="relative" data-testid="tracking-timeline">
          {items.map((t, i) => {
            const isTop = i === 0;
            const last = i === items.length - 1;
            const d = t.timestamp ? new Date(t.timestamp) : null;
            const title = pretty(t.status);
            const showNote = t.note && t.note !== title && t.note !== t.location;
            return (
              <motion.li
                key={t._i}
                className="grid grid-cols-[1fr_auto_1.25fr] gap-x-3.5"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.3 }}
                data-testid={`timeline-item-${t._i}`}
              >
                <div className="text-right pt-0.5">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-foreground leading-tight">{(t.location || "").toUpperCase() || "—"}</div>
                  {d && (
                    <div className="text-[11px] text-muted-foreground leading-tight mt-0.5 font-mono-label">
                      {d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
                      <br />
                      {d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-center">
                  <span className="relative mt-1 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    {isTop && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />}
                    <span className={[
                      "relative h-3.5 w-3.5 rounded-full transition-colors",
                      isTop ? "bg-white border-2 border-emerald-500 shadow" : "bg-emerald-500",
                    ].join(" ")} />
                  </span>
                  {!last && <span className="w-0.5 flex-1 my-1 rounded-full bg-gradient-to-b from-emerald-400 to-primary/40" />}
                </div>

                <div className="pb-6 pt-0.5">
                  <div className="text-sm font-bold uppercase tracking-wide text-foreground leading-tight">{title}</div>
                  {showNote && <div className="text-xs text-muted-foreground mt-0.5 normal-case font-normal">{t.note}</div>}
                </div>
              </motion.li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
